import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { db } from '@/lib/db';

// Auditoria 2026-07-28: o modo 'AUTO' foi removido — nunca teve implementação
// (nenhum branch o tratava; selecioná-lo desligava o painel de sugestão, agindo
// como OFF). Programação automática permanece desativada por decisão operacional.
export type IntelligenceMode = 'OFF' | 'SUGGEST';

export const useIntelligenceMode = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: mode, isLoading } = useQuery({
    queryKey: ['intelligence-mode'],
    queryFn: async () => {
      // maybeSingle (não single): 0 linhas degrada pra 'OFF' SEM throw. O .single()
      // lançava quando a linha do intelligence_mode (presa à loja Matriz) não era
      // encontrada no contexto de outra loja → React Query ia pra erro e o modo
      // ficava preso no initialData 'OFF' (painel de sugestão sumia). O fix de
      // escopo (environment_config ORG-scoped no db.ts) faz a linha ser achada em
      // qualquer loja da org; o maybeSingle é a rede de segurança. Walter 2026-06-14.
      const { data, error } = await db
        .from('environment_config')
        .select('config_value')
        .eq('config_key', 'intelligence_mode')
        .maybeSingle();

      if (error) throw error;
      // Normaliza valores legados (ex.: 'AUTO', que nunca teve implementação):
      // qualquer coisa que não seja 'SUGGEST' se comporta como OFF — igual ao
      // comportamento de sempre do painel de sugestão (que só liga em SUGGEST).
      return (data?.config_value === 'SUGGEST' ? 'SUGGEST' : 'OFF') as IntelligenceMode;
    },
    // Walter 2026-06-14 — REMOVIDO initialData:'OFF'. Com initialData + staleTime,
    // o modo nascia 'OFF' "fresco" e NÃO buscava no mount → o painel de sugestão
    // nascia escondido e só aparecia quando algo disparava um refetch tardio
    // ("inicia não aparecendo e depois passa a aparecer"). Sem initialData, o
    // useConfigSync (global, monta no load do app) busca o modo já na
    // inicialização → quando o modal abre, o modo já está resolvido em cache.
    // `mode` fica undefined só até o 1º fetch resolver (tratado como "carregando",
    // não como OFF, no IntelligenceSuggestionPanel).
    staleTime: 60000, // 60s - config raramente muda
    refetchOnWindowFocus: false,
  });

  const mutation = useMutation({
    mutationFn: async (newMode: IntelligenceMode) => {
      const { error } = await db
        .from('environment_config')
        .update({ 
          config_value: newMode,
          updated_at: new Date().toISOString(),
        })
        .eq('config_key', 'intelligence_mode');
      
      if (error) throw error;
      return newMode;
    },
    onSuccess: (newMode) => {
      queryClient.invalidateQueries({ queryKey: ['intelligence-mode'] });
      toast({
        title: `Modo ${newMode === 'OFF' ? 'Tradicional' : 'Sugestão'} Ativado`,
        description: newMode === 'OFF'
          ? "Sistema funcionando no modo tradicional"
          : "🤖 Sistema vai sugerir programação, você decide",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao alterar modo",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    },
  });

  return {
    mode,
    isLoading,
    setMode: mutation.mutate,
  };
};
